// Data Needed — the judgment-call editors (Category 6 fast entry).
//
// Extracted verbatim from the pre-2026-09-11 DataNeededPage when the page
// became the one-list surface (docs/archive/mockups/data-needed-unified-mockup-v1.html).
// The editors are the behavior the mockup keeps ("Fit is three buttons.
// Numbers are two boxes and Save."): every answer writes the real field
// through the same API calls as before; only the card around them changed.

import { useState } from "react";
import { Link } from "react-router-dom";

import type { JudgmentItemType, MyJudgmentItem } from "../api";
import { recordDeepLink } from "../cleanup/record-links";
import {
  productMetricLabel,
  productMetricHelp,
  revenueFieldLabel,
  revenueHelp,
  REALTY_COMMISSION_METRICS,
} from "@shared/deal-value/metric-label";
import { estimatedFinancialAnnualRevenueCents } from "@shared/deal-value/financial-fee-schedule";
import { LOST_REASON_OPTIONS } from "@shared/opportunities/lost-reasons";
import { DateField } from "../components/DateField";
export const C = {
  navy: "var(--navy)",
  blue: "var(--blue)",
  blueD: "var(--blue-dark)",
  bg: "var(--light-gray)",
  surface: "var(--bg-0)",
  border: "var(--mid-gray)",
  border2: "var(--mid-gray)",
  text: "var(--fg-1)",
  text2: "var(--fg-2)",
  text3: "var(--fg-3)",
  success: "var(--success)",
  danger: "var(--danger)",
};
export const cap = (s: string): string => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export const BORROWER_TIER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "a_plus", label: "A+" },
  { value: "a", label: "A" },
  { value: "a_minus", label: "A-" },
  { value: "b_plus", label: "B+" },
  { value: "b", label: "B" },
  { value: "b_minus", label: "B-" },
  { value: "c_plus", label: "C+" },
  { value: "c", label: "C" },
  { value: "c_minus", label: "C-" },
  { value: "d", label: "D" },
  { value: "other", label: "Other" },
];

// Item types that show the inline "Not sure" (per-user skip) quiet link
// below the editor. Fit assessments use the SAME per-user skip but
// surface it as a peer-level "Don't Know Them Yet" button inside
// FitEditor (Joy's 2026-07-01 feedback), hence fit is not in this set.
// Numbers + borrower-tier joined 2026-07-02 (Joey: migrated Processing
// records with zero source data anywhere — an honest "can't answer this
// yet" beats a made-up number; the item stays open on the team
// scoreboard, exactly like the other skips).
export const SET_ASIDE_LINK_TYPES = new Set<JudgmentItemType>([
  "opportunity_numbers",
  "transaction_numbers",
  "borrower_tier",
  "planning_case_revenue",
  "planning_case_advisor",
]);

// One kind→route map for the whole cleanup/judgment surface. A local
// copy here used to miss the "contact" case, so clicking a contact-grain
// item's name (referral source, preferred name, …) dumped the advisor on
// /cleanup instead of the customer record (Ryan, 2026-07-02).
export function recordHref(kind: string, id: string): string {
  return recordDeepLink(kind, id) ?? "/data-needed";
}

// The mockup's `.inputs input` and `.btn` values (44px targets, the
// mobile-parity floor), so the editors sit inside the card at the same
// scale as the card's own buttons.
export const inputStyle: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  padding: "10px",
  minHeight: 44,
  fontSize: 13,
  fontFamily: "Open Sans, system-ui, sans-serif",
  color: C.text,
  background: C.surface,
};

export const smallBtn: React.CSSProperties = {
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  padding: "12px 14px",
  minHeight: 44,
  fontSize: 10.5,
  fontFamily: "Gotham, Montserrat",
  fontWeight: 800,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  background: C.surface,
  color: C.navy,
  cursor: "pointer",
};

// The action column these editors render into has no width ceiling of its
// own — JudgmentCard's `.dn2-do` and IssueCard's `.ic-type` both size to
// their content, on purpose, so a two-button card stays compact. A wide
// editor breaks that assumption: with no cap, a flex-wrap row's intrinsic
// width is however wide it would be laid out on ONE unbroken line, so five
// money fields in a row measured out to ~950px and squeezed the household
// name beside it into a one-word ribbon (Grant, 2026-09-16). Every editor
// below caps its own width at this constant instead, so it wraps its own
// fields internally rather than stretching the column that holds it.
export const JUDGMENT_EDITOR_MAX_WIDTH = 320;

export function daysAgoLabel(iso: string): string {
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return "recently";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `over ${Math.floor(days / 365)} year${days >= 730 ? "s" : ""} ago`;
}

function fullDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * "WHICH of this household's records is this?" — the line that makes a
 * workflow-record judgment item identifiable.
 *
 * Nate 2026-07-29: on a household Realty has helped buy or sell more than
 * once, "Matthew Kuiper Household · Realty Transaction" names no
 * particular deal, and "there is not an address referred to here." The
 * address is missing because these BrokerMint-era transactions carry no
 * linked property record — so this line leads with the dates the record
 * DOES know (the close date is what he'll match against BrokerMint),
 * says out loud when the household has several records with this company,
 * and points at the fix for the missing address instead of leaving a dead
 * end (Cleanup UX rules 1 + 5).
 */
export function formatUsdWhole(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function RecordIdentityLine({ item }: { item: MyJudgmentItem }) {
  const ctx = item.record_context;
  if (!ctx) return null;

  const company = item.company_slug ? cap(item.company_slug) : "";
  const segments: string[] = [];
  if (ctx.closed_on) {
    segments.push(`Closed ${fullDateLabel(ctx.closed_on)}`);
  } else if (ctx.started_on) {
    segments.push(`Started ${fullDateLabel(ctx.started_on)}`);
  }
  // Stage only while it's still moving — on a closed record the outcome is
  // the state that matters and the question already says "already closed".
  if (!ctx.closed_on && ctx.stage) segments.push(ctx.stage);
  // Sale price / advisor — already folded into the label itself when the
  // address was missing (see attachTransactionRecordLabels), but repeated
  // here so the identity line reads complete on its own without the
  // reader having to cross-reference the title.
  if (!ctx.property_address && ctx.sale_price) {
    segments.push(formatUsdWhole(ctx.sale_price));
  }
  if (ctx.advisor_name) segments.push(ctx.advisor_name);
  if (ctx.company_record_count > 1) {
    segments.push(
      `one of ${ctx.company_record_count} ${company || "company"} records on this household`.trim(),
    );
  }

  if (segments.length === 0 && !ctx.property_link_missing) return null;

  return (
    <div style={{ fontSize: 12, color: C.text3, lineHeight: 1.7, margin: "1px 0 0" }}>
      {segments.map((s, i) => (
        <span key={s}>
          {i > 0 && <span aria-hidden> · </span>}
          {s}
        </span>
      ))}
      {ctx.property_link_missing && (
        <span>
          {segments.length > 0 && <span aria-hidden> · </span>}
          <span>no property address on this record — </span>
          <Link
            to={recordHref(item.record.kind, item.record.id)}
            style={{ color: C.blueD, fontWeight: 600 }}
          >
            add or link the property
          </Link>
        </span>
      )}
    </div>
  );
}

export function NumbersEditor({
  item,
  busy,
  onSave,
  onMarkUnknown,
  onMarkLost,
}: {
  item: MyJudgmentItem;
  busy: boolean;
  onSave: (patch: {
    expected_close_date: string | null;
    product_metric: number | null;
    estimated_revenue_year_1: number | null;
    /** Realty transactions with a linked property only (Grant 2026-07-29):
     *  BrokerMint's three commission metrics, entered verbatim. */
    realty_commission?: {
      office_gross: number | null;
      agent_net: number | null;
      office_net: number | null;
    };
  }) => void;
  onMarkUnknown: () => void;
  // Present only for opportunity_numbers items (Brandon 2026-07-13: a
  // quote he knows won't close shouldn't force a fake close-date/revenue
  // entry — closing it Lost is the honest answer, and it clears the item).
  onMarkLost?: ((reason: string) => void) | undefined;
}) {
  const [showLostPicker, setShowLostPicker] = useState(false);
  const [lostReason, setLostReason] = useState("");

  const isFinancial = item.company_slug === "financial";
  // Realty transactions record the commission as BrokerMint's three
  // metrics, verbatim (Grant 2026-07-29: "move from less the 10% to the
  // three metrics BrokerMint gives us") — entry is a copy-across from the
  // BrokerMint tab, not arithmetic. The breakdown's storage home is the
  // LINKED property record, so the three-field form appears only when one
  // is linked; unlinked records take Office net alone (it lives on the
  // transaction) plus the existing "add or link the property" pointer.
  const isRealtyTxn =
    item.company_slug === "realty" && item.item_type === "transaction_numbers";
  const linkedCommission = isRealtyTxn
    ? (item.record_context?.realty_commission ?? null)
    : null;
  // A closed deal's expected close date is moot (actual_close_date answers
  // "when") — the detector already skips it, so the editor does too.
  const closedAlready =
    item.item_type === "transaction_numbers" &&
    item.record_context?.closed_on != null;
  const [gross, setGross] = useState(
    linkedCommission?.office_gross != null
      ? String(linkedCommission.office_gross)
      : "",
  );
  const [agentNet, setAgentNet] = useState(
    linkedCommission?.agent_net != null
      ? String(linkedCommission.agent_net)
      : "",
  );

  // Financial Year-1 revenue is derivable from AUM via the documented fee
  // schedule. Suggest it from the entered metric (dollars) — "" when the
  // metric is blank/invalid or the company isn't Financial. The advisor can
  // still overwrite it (Grant 2026-07-10: auto-fill, but keep it editable).
  const suggestRevenue = (metricStr: string): string => {
    if (!isFinancial) return "";
    const aum = Number(metricStr.trim());
    if (!Number.isFinite(aum) || aum <= 0) return "";
    return String(Math.round(estimatedFinancialAnnualRevenueCents(aum * 100) / 100));
  };

  const [close, setClose] = useState(item.numbers?.expected_close_date ?? "");
  const initialMetric =
    item.numbers?.product_metric != null ? String(item.numbers.product_metric) : "";
  const [metric, setMetric] = useState(initialMetric);
  // A revenue already on the record is authoritative (someone typed it, or a
  // prior save). Only auto-fill when it's blank. `revenueTouched` tracks
  // whether the value is user/record-owned so metric edits never clobber it.
  const [revenueTouched, setRevenueTouched] = useState(
    item.numbers?.estimated_revenue_year_1 != null,
  );
  const [revenue, setRevenue] = useState(
    item.numbers?.estimated_revenue_year_1 != null &&
      item.numbers.estimated_revenue_year_1 !== 0
      ? String(item.numbers.estimated_revenue_year_1)
      : linkedCommission?.office_net != null && linkedCommission.office_net > 0
        ? String(linkedCommission.office_net)
        : suggestRevenue(initialMetric),
  );
  const autoFilled = isFinancial && !revenueTouched && revenue !== "";

  const onMetricChange = (next: string) => {
    setMetric(next);
    // Keep an un-touched Financial revenue in sync with the AUM the advisor
    // is typing; once they edit revenue themselves we stop.
    if (isFinancial && !revenueTouched) {
      setRevenue(suggestRevenue(next));
    }
  };

  const chip = (label: string, iso: string) => (
    <button
      type="button"
      className="dn-btn dn-btn-chip"
      key={label}
      disabled={busy}
      onClick={() => setClose(iso)}
      style={{ ...smallBtn, padding: "4px 8px", fontSize: 10.5, textTransform: "none", fontFamily: "Open Sans", fontWeight: 600, color: "var(--blue-ink)" }}
    >
      {label}
    </button>
  );
  const today = new Date();
  const plus30 = new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10);
  const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const eomIso = `${eom.getFullYear()}-${String(eom.getMonth() + 1).padStart(2, "0")}-${String(eom.getDate()).padStart(2, "0")}`;

  const toNum = (s: string): number | null => {
    const t = s.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  // Nate 2026-07-02: "zero idea what Year 1 Revenue means… so this
  // probably just means the commission." Grant 2026-07-10: ask in the
  // company's own language — a Financial record's metric is AUM, not a
  // generic "Product metric". Label + help now come from the shared
  // deal-value module (help on tap, never jargon alone — Cleanup UX #9)
  // so this editor and the record detail rail can never drift. Realty's
  // help answers Nate's 2026-07-29 gross-vs-net question in place.
  const revenueLabelText = revenueFieldLabel(item.company_slug, " ($)");
  const revenueHelpText = revenueHelp(item.company_slug);
  const metricHelp = productMetricHelp(item.company_slug);
  const metricFieldLabel = `${productMetricLabel(item.company_slug)} ($)`;

  // Realty txn: the revenue field IS BrokerMint's Office net, labeled with
  // BrokerMint's own name so it reads as a copy-across.
  const officeNetDef = REALTY_COMMISSION_METRICS.find(
    (m) => m.key === "office_net",
  )!;
  const grossDef = REALTY_COMMISSION_METRICS.find(
    (m) => m.key === "office_gross",
  )!;
  const agentNetDef = REALTY_COMMISSION_METRICS.find(
    (m) => m.key === "agent_net",
  )!;
  const effectiveRevenueLabel = isRealtyTxn
    ? `${officeNetDef.label} ($)`
    : revenueLabelText;
  const effectiveRevenueHelp = isRealtyTxn ? officeNetDef.help : revenueHelpText;

  const moneyField = (opts: {
    label: string;
    help: string;
    value: string;
    onChange: (v: string) => void;
    width?: number;
    placeholder?: string;
  }) => (
    <label
      title={opts.help}
      style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: C.text3, fontWeight: 600 }}
    >
      <span>
        {opts.label}{" "}
        <span aria-label={opts.help} style={{ cursor: "help", borderBottom: `1px dotted ${C.text3}` }}>?</span>
      </span>
      <input
        type="number"
        min={0}
        disabled={busy}
        value={opts.value}
        onChange={(e) => opts.onChange(e.target.value)}
        style={{ ...inputStyle, width: opts.width ?? 110 }}
        placeholder={opts.placeholder}
      />
    </label>
  );

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", maxWidth: JUDGMENT_EDITOR_MAX_WIDTH }}>
      {!closedAlready && (
        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: C.text3, fontWeight: 600 }}>
          Expected close
          <DateField disabled={busy} value={close} onChange={setClose} style={inputStyle} />
          <span style={{ display: "flex", gap: 4 }}>
            {chip("+30 days", plus30)}
            {chip("End of month", eomIso)}
          </span>
        </label>
      )}
      <label
        title={metricHelp}
        style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: C.text3, fontWeight: 600 }}
      >
        <span>
          {metricFieldLabel}{" "}
          <span aria-label={metricHelp} style={{ cursor: "help", borderBottom: `1px dotted ${C.text3}` }}>?</span>
        </span>
        <input type="number" min={0} disabled={busy} value={metric} onChange={(e) => onMetricChange(e.target.value)} style={{ ...inputStyle, width: 120 }} placeholder="e.g. 450000" />
      </label>
      {/* Grant 2026-07-29: a Realty deal with a linked property record takes
          BrokerMint's full three-metric breakdown, copied verbatim from
          BrokerMint's columns. Unlinked records take Office net alone — the
          breakdown's storage home is the property record, and the "add or
          link the property" pointer above is the path to it. */}
      {linkedCommission && (
        <>
          {moneyField({
            label: `${grossDef.label} ($)`,
            help: grossDef.help,
            value: gross,
            onChange: setGross,
            placeholder: "e.g. 15000",
          })}
          {moneyField({
            label: `${agentNetDef.label} ($)`,
            help: agentNetDef.help,
            value: agentNet,
            onChange: setAgentNet,
            placeholder: "e.g. 4500",
          })}
        </>
      )}
      <label
        title={effectiveRevenueHelp}
        style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11, color: C.text3, fontWeight: 600 }}
      >
        <span>
          {effectiveRevenueLabel}{" "}
          <span aria-label={effectiveRevenueHelp} style={{ cursor: "help", borderBottom: `1px dotted ${C.text3}` }}>?</span>
        </span>
        <input
          type="number"
          min={0}
          disabled={busy}
          value={revenue}
          onChange={(e) => {
            setRevenue(e.target.value);
            setRevenueTouched(true);
          }}
          style={{ ...inputStyle, width: isRealtyTxn ? 140 : 110 }}
          placeholder="e.g. 3800"
        />
        {autoFilled ? (
          <span style={{ fontSize: 9.5, fontWeight: 600, color: C.text3, textTransform: "none", fontFamily: "Open Sans" }}>
            Auto-filled from the Financial AUM fee schedule — edit if it closed differently.
          </span>
        ) : null}
        {isRealtyTxn && !linkedCommission ? (
          <span style={{ fontSize: 9.5, fontWeight: 600, color: C.text3, textTransform: "none", fontFamily: "Open Sans", maxWidth: 200 }}>
            Link the property to record the full BrokerMint breakdown (Office
            gross / Agent net) too.
          </span>
        ) : null}
      </label>
      <button
        type="button"
        className="dn-btn"
        disabled={busy}
        style={{ ...smallBtn, background: `linear-gradient(135deg,${C.blue},${C.blueD})`, color: "var(--white)", border: "none" }}
        onClick={() =>
          onSave({
            expected_close_date: closedAlready
              ? (item.numbers?.expected_close_date ?? null)
              : close.trim() === ""
                ? null
                : close,
            product_metric: toNum(metric),
            estimated_revenue_year_1: toNum(revenue),
            ...(linkedCommission
              ? {
                  realty_commission: {
                    office_gross: toNum(gross),
                    agent_net: toNum(agentNet),
                    office_net: toNum(revenue),
                  },
                }
              : {}),
          })
        }
      >
        Save
      </button>
      {/* Honest terminal answer, mirroring WMM's "don't know this family":
          when the numbers genuinely can't be reconstructed (migrated deal
          with nothing in the source), record that and clear the item for
          everyone — not the per-user "Not sure" set-aside that leaves it
          open on the team scoreboard forever (Grant 2026-07-11). */}
      <button
        type="button"
        className="dn-btn"
        disabled={busy}
        title="Record that these numbers aren't knowable for this record. Removes it from Data Needed for the whole team; reversible on the record."
        style={{ ...smallBtn, borderColor: "transparent", color: C.text3, textDecoration: "underline", background: "none" }}
        onClick={() => onMarkUnknown()}
      >
        Not known / N/A
      </button>
      {/* Brandon's 2026-07-13 feedback: a quote he already knows is dead
          (e.g. sitting in Final Ask with no response) shouldn't force a
          fake close-date/revenue guess. Marking it Lost is the honest
          disposition and auto-clears the item via the same
          clearJudgmentOnOppClose the opportunity detail page's Lost
          button triggers. */}
      {onMarkLost &&
        (showLostPicker ? (
          <span style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <select
              aria-label="Why was it lost?"
              disabled={busy}
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              style={{ ...inputStyle, width: 170 }}
            >
              <option value="">Pick a reason…</option>
              {LOST_REASON_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="dn-btn"
              disabled={busy || !lostReason}
              style={{ ...smallBtn, borderColor: C.danger, color: C.danger, background: "none" }}
              onClick={() => onMarkLost(lostReason)}
            >
              Confirm lost
            </button>
            <button
              type="button"
              className="dn-btn"
              disabled={busy}
              style={{ ...smallBtn, background: "none" }}
              onClick={() => {
                setShowLostPicker(false);
                setLostReason("");
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="dn-btn"
            disabled={busy}
            title="Close this quote as Lost — clears it from Data Needed. No service record is created."
            style={{ ...smallBtn, borderColor: C.danger, color: C.danger, background: "none" }}
            onClick={() => setShowLostPicker(true)}
          >
            Mark lost
          </button>
        ))}
    </div>
  );
}

export function WmmEditor({
  busy,
  onSave,
  onMarkUnknown,
}: {
  busy: boolean;
  onSave: (text: string) => void;
  onMarkUnknown: () => void;
}) {
  const [text, setText] = useState("");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", maxWidth: JUDGMENT_EDITOR_MAX_WIDTH }}>
      <input
        type="text"
        disabled={busy}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder='e.g. "Retire at 60 · get all three kids through college debt-free"'
        style={{ ...inputStyle, flex: 1, minWidth: 260 }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && text.trim() !== "") onSave(text.trim());
        }}
      />
      <button
        type="button"
        className="dn-btn"
        disabled={busy || text.trim() === ""}
        style={{ ...smallBtn, background: `linear-gradient(135deg,${C.blue},${C.blueD})`, color: "var(--white)", border: "none" }}
        onClick={() => onSave(text.trim())}
      >
        Save
      </button>
      {/* Honest escape hatch, mirroring fit's "Can't assess — advisor
          left": records that we don't know this family well enough so the
          item clears without a fake answer. */}
      <button
        type="button"
        className="dn-btn"
        disabled={busy}
        title="Record that you don't know this family well enough to answer. Removes it from Data Needed; reversible on the household page."
        style={{ ...smallBtn, borderColor: "transparent", color: C.text3, textDecoration: "underline", background: "none" }}
        onClick={() => onMarkUnknown()}
      >
        Don't know this client well enough
      </button>
    </div>
  );
}

// ─── Small shared bits ───────────────────────────────────────────────
